import {
  CloudDownloadOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  PlusOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import {
  ContextButton,
  ExportButton,
  IconContextButton,
  ScopeButton,
} from '@monorepo/shared-lib';
import { Input, Popover, Space } from 'antd';
import { ReactComponent as EllipseIcon } from 'assets/images/icons/ellipsesIcon.svg';
import { ReactComponent as IconSupport } from 'assets/images/icons/iconSupport.svg';
import { StyledCard } from 'components/Custom/card';
import { StyledPopconfirm } from 'components/Custom/Popconfirm';
import { ProviderIcon } from 'components/Custom/ProviderIcon';
import {
  ScopeDropdownButton,
  ScopeEditCancelIcon,
  ScopeEditSaveButton,
} from 'components/Custom/ScopeDropdownButton';
import { CardTitleLargeSpan } from 'components/Custom/spans';
import { ScopeTable } from 'components/Custom/table';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Provider } from 'store/api/enums';
import { turnDrawerOff, turnDrawerOn } from 'store/app/actions';
import { DrawerType, TaxRateStatus } from 'store/app/enums';
import { DrawerData } from 'store/app/types';
import { getOrgOrganisations } from 'store/organisation/selectors';
import { fetchReturnTypesRequest } from 'store/returns/actions';
import { TaxRate } from 'store/returns/models/returnData';
import { getReturnLoading } from 'store/returns/selectors';
import {
  deleteExcelTaxRateRequest,
  getTaxRateTemplatesRequest,
  saveTaxRateTemplateRequest,
  updateExcelTaxRateNameRequest,
} from 'store/tax-rates/actions';
import { TaxRateTemplateToSave } from 'store/tax-rates/models';
import {
  getTaxRatesForConnection,
  getTaxRatesGettingTaxes,
  getTaxRateNameUpdateFailureTaxType,
  getTaxRateTemplates,
} from 'store/tax-rates/selectors';
import { Preference, PreferenceActions } from 'utils/preferences';
import { StatusGetter } from 'utils/status-getter';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { AddCustomTaxRate } from './add-custom-tax-rate';
import { AddNewTaxesForm } from './add-new-taxes-form';
import { CreateTaxTemplate } from './create-tax-template';
import { EntityNarrowSidePanelChrome } from './entity-narrow-side-panel-chrome';
import { EntityGuide } from './tax-rate-guide-new';
import {
  TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH,
  TaxRatesPromotionBanner,
} from './tax-rates-promotion-banner';

// Provider icon mapping
export const TAX_RATES_TUTORIAL_HIDE = 'tax-rates-tutorial-hide';

const ENTITY_DETAILS_BREAKPOINT = 1500;
const ENTITY_DETAILS_HEIGHT_BREAKPOINT = 750;

interface TaxRatesTabProps {
  organisation?: any;
  handlePreferences: (action: PreferenceActions, preference: Preference) => any;
}

export const TaxRatesTab: React.FC<TaxRatesTabProps> = ({
  organisation,
  handlePreferences,
}) => {
  const { t } = useTranslation();
  const pwaMobileNav = usePwaMobileNavEnabled();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const taxRatesWideLayout =
    windowWidth > ENTITY_DETAILS_BREAKPOINT &&
    windowHeight > ENTITY_DETAILS_HEIGHT_BREAKPOINT;
  const [showTaxRatesPromoNarrowPanel, setShowTaxRatesPromoNarrowPanel] =
    React.useState(false);
  React.useEffect(() => {
    if (taxRatesWideLayout) {
      setShowTaxRatesPromoNarrowPanel(false);
    } else {
      setShowCard(false);
    }
  }, [taxRatesWideLayout]);
  const dispatch = useDispatch();
  const connectionId = organisation?.connectionId ?? '';
  const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([]);
  const guidePanelHide = handlePreferences(PreferenceActions.GET, {
    name: TAX_RATES_TUTORIAL_HIDE,
  });
  const [showCard, setShowCard] = React.useState(
    !guidePanelHide && taxRatesWideLayout && !pwaMobileNav
  );
  const gettingTaxes = useSelector(getTaxRatesGettingTaxes);
  const taxRateNameUpdateFailureTaxType = useSelector(
    getTaxRateNameUpdateFailureTaxType
  );
  const organisations = useSelector(getOrgOrganisations);
  const liveOrganisation = React.useMemo(() => {
    if (!organisations?.length) return organisation;
    return (
      organisations.find(
        (o) =>
          (connectionId && o.connectionId === connectionId) ||
          (organisation?.uuid && o.uuid === organisation.uuid)
      ) ?? organisation
    );
  }, [organisations, connectionId, organisation]);
  const storedTaxRatesSelector = React.useMemo(
    () => getTaxRatesForConnection(connectionId),
    [connectionId]
  );
  const storedTaxRates = useSelector(storedTaxRatesSelector);
  const returnLoading = useSelector(getReturnLoading);
  const [showAllTaxRates, setShowAllTaxRates] = React.useState(false);
  const [menuVisible, setMenuVisible] = React.useState(false);
  const [editingTaxRateKey, setEditingTaxRateKey] = React.useState<
    string | null
  >(null);
  const [editingDraftName, setEditingDraftName] = React.useState('');
  const [optimisticNames, setOptimisticNames] = React.useState<
    Record<string, string>
  >({});

  const isExcelEntity =
    liveOrganisation?.providerName?.toLowerCase() ===
    Provider.EXCEL?.toLowerCase();
  const isReceiptsEntity =
    liveOrganisation?.providerName?.toLowerCase() ===
    Provider.RECEIPTS?.toLowerCase();
  const isExcelOrReceiptsEntity = isExcelEntity || isReceiptsEntity;

  const taxRatesForOrganisation = React.useMemo(() => {
    if (isExcelOrReceiptsEntity) {
      if (storedTaxRates.length > 0) return storedTaxRates;
      return liveOrganisation?.taxRates ?? [];
    }
    if (
      liveOrganisation?.taxRatesUnique &&
      liveOrganisation.taxRatesUnique.length > 0
    ) {
      return liveOrganisation.taxRatesUnique;
    }
    return liveOrganisation?.taxRates ?? [];
  }, [isExcelOrReceiptsEntity, storedTaxRates, liveOrganisation]);

  React.useEffect(() => {
    if (!taxRateNameUpdateFailureTaxType) return;

    setOptimisticNames((prev) => {
      if (!prev[taxRateNameUpdateFailureTaxType]) return prev;
      const next = { ...prev };
      delete next[taxRateNameUpdateFailureTaxType];
      return next;
    });
  }, [taxRateNameUpdateFailureTaxType]);

  const templates = useSelector(getTaxRateTemplates) || [];
  const hasTemplates = Array.isArray(templates) && templates.length > 0;

  // Get the provider-specific tax rates link
  const getTaxRatesLink = React.useMemo(() => {
    const providerName = organisation?.providerName?.toLowerCase();
    if (providerName === Provider.QUICKBOOKS) {
      return 'https://qbo.intuit.com/app/tax/home';
    } else if (providerName === Provider.ZOHO) {
      return `https://books.zoho.eu/app/${organisation?.shortCode}#/settings/taxes/taxrates`;
    }
    // Default to Xero link
    return 'https://go.xero.com/Setup/TaxRates.aspx';
  }, [organisation?.providerName]);

  // Create taxRates with proper keys
  const taxRates = React.useMemo(() => {
    return (
      taxRatesForOrganisation?.map((taxRate: TaxRate) => ({
        ...taxRate,
        key: taxRate.taxType,
      })) || []
    );
  }, [taxRatesForOrganisation]);

  React.useEffect(() => {
    setOptimisticNames((prev) => {
      if (Object.keys(prev).length === 0) return prev;

      const next = { ...prev };
      let changed = false;

      for (const taxType of Object.keys(prev)) {
        const rate = taxRates.find((item) => item.taxType === taxType);
        if (rate && (rate.name || '').trim() === prev[taxType]) {
          delete next[taxType];
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [taxRates]);

  // Filter taxRates based on switch
  const filteredTaxRates = React.useMemo(() => {
    if (showAllTaxRates) return taxRates;
    return taxRates.filter((rate) => {
      const status = (rate as any).status;
      return !status || status === TaxRateStatus.ACTIVE || status === 'ACTIVE';
    });
  }, [taxRates, showAllTaxRates]);

  const onProceedTaxes = () => {
    dispatch(getTaxRateTemplatesRequest());
    dispatch(fetchReturnTypesRequest(organisation?.connectionId));
    const entityCountryId = organisation?.returnTypesEdit?.[0]?.countryId;
    const drawerData: DrawerData = {
      title: t('organisation.add-new-taxes'),
      type: DrawerType.ADD_NEW_TAXES,
      component: (
        <AddNewTaxesForm
          loading={gettingTaxes || returnLoading}
          connectionId={organisation?.connectionId}
          initialCountryId={entityCountryId}
        />
      ),
    };
    dispatch(turnDrawerOn(drawerData));
  };

  const onCreateTaxTemplate = (editMode: boolean = false) => {
    // Explicit "Manage Templates" / "Create Template" entry from the context menu.
    // This preserves the previous behavior when invoked from the ellipsis menu.
    dispatch(getTaxRateTemplatesRequest());

    const saveTemplate = (templateName: string) => {
      const template: TaxRateTemplateToSave = {
        name: templateName,
        rates: selectedRowKeys.map((key) => key.toString()),
      };
      dispatch(turnDrawerOff());
      dispatch(saveTaxRateTemplateRequest(organisation.connectionId, template));
    };

    const drawerData: DrawerData = {
      title: editMode
        ? t('organisation.manage-templates')
        : t('organisation.create-template'),
      type: DrawerType.CREATE_TAX_TEMPLATE,
      component: (
        <CreateTaxTemplate onSaveTemplate={saveTemplate} editMode={editMode} />
      ),
    };
    dispatch(turnDrawerOn(drawerData));
  };

  const handleTaxTemplateButtonClick = () => {
    if (!connectionId) return;

    // Always refresh templates before opening the drawer
    dispatch(getTaxRateTemplatesRequest());

    if (selectedRowKeys.length === 0) {
      // No rows selected -> behave like "Manage Templates"
      const drawerData: DrawerData = {
        title: t('organisation.manage-templates'),
        type: DrawerType.CREATE_TAX_TEMPLATE,
        component: (
          <CreateTaxTemplate onSaveTemplate={() => {}} editMode={true} />
        ),
      };
      dispatch(turnDrawerOn(drawerData));
      return;
    }

    // Rows selected -> behave like "Create Template" from current selection
    const saveTemplate = (templateName: string) => {
      const template: TaxRateTemplateToSave = {
        name: templateName,
        rates: selectedRowKeys.map((key) => key.toString()),
      };
      dispatch(turnDrawerOff());
      dispatch(saveTaxRateTemplateRequest(connectionId, template));
      setSelectedRowKeys([]);
    };

    const drawerData: DrawerData = {
      title: t('organisation.create-template'),
      type: DrawerType.CREATE_TAX_TEMPLATE,
      component: (
        <CreateTaxTemplate onSaveTemplate={saveTemplate} editMode={false} />
      ),
    };
    dispatch(turnDrawerOn(drawerData));
  };

  const renderContextMenu = () => {
    // Check if there are any inactive tax rates
    const hasInactiveRates = taxRates.some((rate) => {
      const status = (rate as any).status;
      return status && status !== TaxRateStatus.ACTIVE && status !== 'ACTIVE';
    });

    return (
      <div className="context-menu-items">
        <Space direction="vertical" style={{ rowGap: '0' }}>
          <IconContextButton
            type="text"
            icon={<SettingOutlined style={{ width: '18px', height: '18px' }} />}
            onClick={() => {
              onCreateTaxTemplate(true);
              setMenuVisible(false);
            }}
          >
            {t('organisation.manage-templates')}
          </IconContextButton>
          <IconContextButton
            type="text"
            icon={
              <CloudDownloadOutlined
                style={{ width: '18px', height: '18px' }}
              />
            }
            onClick={() => {
              onProceedTaxes();
              setMenuVisible(false);
            }}
          >
            {t('organisation.load-tax-rates')}
          </IconContextButton>
          <IconContextButton
            type="text"
            icon={
              showAllTaxRates ? (
                <EyeInvisibleOutlined
                  style={{ width: '18px', height: '18px' }}
                />
              ) : (
                <EyeOutlined style={{ width: '18px', height: '18px' }} />
              )
            }
            onClick={() => {
              if (hasInactiveRates) {
                setShowAllTaxRates(!showAllTaxRates);
                setMenuVisible(false);
              }
            }}
            disabled={!hasInactiveRates}
          >
            {showAllTaxRates
              ? t('organisation.hide-inactive')
              : t('organisation.show-inactive')}
          </IconContextButton>
        </Space>
      </div>
    );
  };

  const renderBulkButton = () => {
    return (
      <Space direction="vertical" align="end" size={10}>
        <Popover
          placement="topRight"
          arrow={false}
          trigger="click"
          content={renderContextMenu()}
          title={''}
          overlayStyle={{ zIndex: 9999 }}
          open={menuVisible}
          onOpenChange={setMenuVisible}
          destroyOnHidden={true}
        >
          <ContextButton type="text" aria-label={t('common.more-actions')}>
            <EllipseIcon
              style={{
                width: 18,
                height: 18,
              }}
            />
          </ContextButton>
        </Popover>
        <Space size={8}>
          <ScopeButton
            type="default"
            width="fit-content"
            height="32px"
            fontSize="13px"
            lineheight="20px"
            onClick={handleTaxTemplateButtonClick}
            disabled={
              !connectionId || (selectedRowKeys.length === 0 && !hasTemplates)
            }
          >
            {selectedRowKeys.length === 0
              ? t('organisation.manage-templates')
              : t('organisation.create-template')}
            {selectedRowKeys.length > 0 ? ` (${selectedRowKeys.length})` : ''}
          </ScopeButton>
          {isExcelOrReceiptsEntity && (
            <ScopeButton
              type="primary"
              width="fit-content"
              height="32px"
              fontSize="13px"
              lineheight="20px"
              onClick={() => {
                if (!connectionId) return;
                const drawerData: DrawerData = {
                  title: t('organisation.add-tax-rate'),
                  type: DrawerType.CREATE_TAX_TEMPLATE,
                  component: <AddCustomTaxRate connectionId={connectionId} />,
                  width: 420,
                };
                dispatch(turnDrawerOn(drawerData));
              }}
              disabled={!connectionId}
            >
              <PlusOutlined style={{ marginRight: 6 }} />
              {t('organisation.add-tax-rate')}
            </ScopeButton>
          )}
        </Space>
      </Space>
    );
  };

  const handleEditTaxRate = React.useCallback(
    (record: TaxRate) => {
      if (!isExcelOrReceiptsEntity) return;
      setEditingTaxRateKey(record.taxType);
      setEditingDraftName(record.name ?? '');
    },
    [isExcelOrReceiptsEntity]
  );

  const handleCancelEditTaxRate = React.useCallback(() => {
    setEditingTaxRateKey(null);
    setEditingDraftName('');
  }, []);

  const handleSaveTaxRate = React.useCallback(
    (record: TaxRate) => {
      if (!connectionId) {
        setEditingTaxRateKey(null);
        setEditingDraftName('');
        return;
      }

      const trimmedName = (editingDraftName || '').trim();
      if (!trimmedName) {
        // Do not send empty name; just exit edit mode
        setEditingTaxRateKey(null);
        setEditingDraftName('');
        return;
      }

      const originalName = (record.name || '').trim();
      if (trimmedName === originalName) {
        // No changes made; just exit edit mode without dispatching
        setEditingTaxRateKey(null);
        setEditingDraftName('');
        return;
      }

      // Optimistically update the displayed name so we don't briefly show
      // the old value while waiting for the backend refresh to complete.
      setOptimisticNames((prev) => ({
        ...prev,
        [record.taxType]: trimmedName,
      }));

      // Only Excel / Receipts entities support inline name update via our new API
      if (isExcelOrReceiptsEntity && record.id) {
        dispatch(
          updateExcelTaxRateNameRequest(
            connectionId,
            record.id,
            trimmedName,
            record.taxType
          )
        );
      }

      setEditingTaxRateKey(null);
      setEditingDraftName('');
    },
    [
      connectionId,
      dispatch,
      editingDraftName,
      isExcelOrReceiptsEntity,
      setEditingTaxRateKey,
      setEditingDraftName,
      setOptimisticNames,
    ]
  );

  const onDeleteTaxRate = React.useCallback(
    (record: TaxRate) => {
      if (!connectionId || !record.id) return;
      if (!isExcelOrReceiptsEntity) return;
      dispatch(deleteExcelTaxRateRequest(connectionId, record.id));
    },
    [dispatch, connectionId, isExcelOrReceiptsEntity]
  );

  const columns = React.useMemo(() => {
    const baseColumns: any[] = [
      {
        title: t('organisation.tax-name'),
        dataIndex: 'name',
        key: 'name',
        render: (value: string, record: TaxRate) => {
          const isEditing = editingTaxRateKey === record.taxType;
          if (isEditing && isExcelOrReceiptsEntity) {
            return (
              <Input
                size="small"
                value={editingDraftName}
                onChange={(e) => setEditingDraftName(e.target.value)}
                style={{ width: '100%', minWidth: 120 }}
                autoFocus
              />
            );
          }
          const displayValue = optimisticNames[record.taxType] ?? value ?? '-';
          return (
            <span
              role={isExcelOrReceiptsEntity ? 'button' : undefined}
              tabIndex={isExcelOrReceiptsEntity ? 0 : -1}
              onDoubleClick={
                isExcelOrReceiptsEntity
                  ? () => handleEditTaxRate(record)
                  : undefined
              }
              onKeyDown={
                isExcelOrReceiptsEntity
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleEditTaxRate(record);
                      }
                    }
                  : undefined
              }
              style={{
                display: 'block',
                width: '100%',
                cursor: isExcelOrReceiptsEntity ? 'default' : 'text',
              }}
            >
              {displayValue}
            </span>
          );
        },
      },
      {
        title: t('transactions.taxRate'),
        dataIndex: 'taxRate',
        key: 'taxRate',
        render: (value: string | number | null | undefined) =>
          value != null && value !== '' ? `${value}%` : '-',
      },
      {
        title: t('main.status'),
        dataIndex: 'status',
        key: 'status',
        width: 150,
        render: (content: string) => {
          if (content == null || String(content).trim() === '') {
            return <span style={{ color: 'var(--color-text-muted, #5a6a6a)' }}>-</span>;
          }
          return <StatusGetter status={content} noDraft />;
        },
      },
    ];

    if (!isExcelOrReceiptsEntity) {
      return baseColumns;
    }

    return [
      ...baseColumns,
      {
        title: '',
        key: 'actions',
        width: 120,
        render: (_: unknown, record: TaxRate) => {
          const isEditing = editingTaxRateKey === record.taxType;
          if (isEditing) {
            return (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  width: '100%',
                  gap: 6,
                }}
              >
                <ScopeEditSaveButton
                  type="default"
                  onClick={() => handleSaveTaxRate(record)}
                  aria-label={t('common.save')}
                >
                  {t('common.save')}
                </ScopeEditSaveButton>
                <ScopeEditCancelIcon
                  onClick={handleCancelEditTaxRate}
                  role="button"
                  tabIndex={0}
                  aria-label={t('common.cancel-editing')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleCancelEditTaxRate();
                    }
                  }}
                />
              </div>
            );
          }
          return (
            <ScopeDropdownButton
              label={t('common.edit')}
              menu={{
                items: [
                  {
                    key: 'edit',
                    label: t('organisation.edit-tax-name'),
                    icon: <EditOutlined style={{ fontSize: 16 }} />,
                    onClick: () => handleEditTaxRate(record),
                  },
                  {
                    key: 'delete',
                    label: (
                      <StyledPopconfirm
                        title={t('organisation.delete-this-tax-rate-title')}
                        onConfirm={() => onDeleteTaxRate(record)}
                        okText={t('common.delete')}
                        cancelText={t('common.cancel')}
                        placement="left"
                      >
                        <span>{t('organisation.delete-tax-rate')}</span>
                      </StyledPopconfirm>
                    ),
                    icon: <DeleteOutlined style={{ fontSize: 16 }} />,
                    danger: true,
                    // Actual delete is triggered only from Popconfirm onConfirm
                    onClick: () => {},
                  },
                ],
              }}
              onClick={() => handleEditTaxRate(record)}
              placement="bottomRight"
            />
          );
        },
      },
    ];
  }, [
    handleEditTaxRate,
    handleCancelEditTaxRate,
    handleSaveTaxRate,
    onDeleteTaxRate,
    editingTaxRateKey,
    editingDraftName,
    isExcelOrReceiptsEntity,
    optimisticNames,
  ]);

  const taxRatesTableCard = (
    <StyledCard
      style={{
        width: '100%',
        maxWidth: '100%',
        minWidth: taxRatesWideLayout ? 600 : 0,
        ...(!taxRatesWideLayout ? { overflowX: 'auto' as const } : {}),
      }}
    >
      <div
        style={{
          padding: '5px',
          paddingLeft: '5px',
          paddingTop: '5px',
          minWidth: taxRatesWideLayout ? 500 : 0,
          margin: 'auto',
        }}
      >
        <ScopeTable
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys: selectedRowKeys,
            onChange: (selectedRowKeys: React.Key[]) => {
              setSelectedRowKeys(selectedRowKeys);
            },
            columnTitle: (originalNode) =>
              React.isValidElement(originalNode)
                ? React.cloneElement(
                    originalNode as React.ReactElement<{
                      'aria-label'?: string;
                    }>,
                    { 'aria-label': t('common.select-all') }
                  )
                : originalNode,
            getCheckboxProps: (record: TaxRate) => ({
              name: record.name,
              'aria-label': record.name,
            }),
          }}
          mainLocaleKey="tax-rates"
          tableDataSource={filteredTaxRates}
          originalDataSource={filteredTaxRates}
          columns={columns}
          pagination={false}
          rowsPerPage={0}
          enableColumnsCustomization={false}
          transactionType=""
          reportId=""
          headerFontSize={12}

          titleComponent={
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  minHeight: '32px',
                }}
              >
                <CardTitleLargeSpan
                  style={{ fontSize: 20, lineHeight: '32px' }}
                >
                  {t('organisation.current-tax-rates')}
                </CardTitleLargeSpan>
                {organisation?.providerName &&
                  (isExcelOrReceiptsEntity ? (
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        height: '32px',
                      }}
                    >
                      <ProviderIcon
                        providerName={organisation.providerName}
                        alt={organisation?.providerName}
                        width={25}
                        style={{ paddingTop: '0' }}
                      />
                    </span>
                  ) : (
                    <a
                      href={getTaxRatesLink}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        height: '32px',
                      }}
                    >
                      <ProviderIcon
                        providerName={organisation.providerName}
                        alt={organisation?.providerName}
                        width={25}
                        style={{ paddingTop: '0' }}
                      />
                    </a>
                  ))}
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: 15,
                }}
              >
                {!showCard && !pwaMobileNav ? (
                  <ExportButton
                    type="text"
                    onClick={() => {
                      setShowCard(true);
                      handlePreferences(PreferenceActions.SET, {
                        name: TAX_RATES_TUTORIAL_HIDE,
                        value: false,
                      });
                    }}
                    icon={<IconSupport style={{ width: 20, height: 20 }} />} //@ts-ignore
                    isGuide
                  >
                    Open Guide
                  </ExportButton>
                ) : null}
                {renderBulkButton()}
              </div>
            </div>
          }
          rowHeight={40}
          showSizeChanger
          getNewPageSize={() => {}}
          scroll={{ x: 'max-content' }}
          fontSize={12}
        />
      </div>
    </StyledCard>
  );

  return (
    <div
      className="entity-tax-rates-tab"
      style={{ minHeight: 'calc(100vh - 120px)' }}
    >
      <div
        className={`tax-rates-tab-wrapper${
          !taxRatesWideLayout ? ' tax-rates-tab-wrapper--narrow' : ''
        }`}
      >
        {showCard && !pwaMobileNav ? (
          <EntityGuide
            setShowCard={setShowCard}
            handlePreferences={handlePreferences}
          />
        ) : null}
        {taxRatesWideLayout ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              gap: 20,
              alignContent: 'end',
            }}
          >
            {taxRatesTableCard}
            <TaxRatesPromotionBanner organisation={organisation} />
          </div>
        ) : (
          <div className="entity-overview-tab entity-details-narrow-side-shell">
            <div
              style={{
                width: '100%',
                minWidth: 850,
                marginRight: '24px',
              }}
            >
              <div
                style={{
                  padding: '0 40px',
                  width: '100%',
                  maxWidth: 1250,
                  margin: '0 auto',
                }}
              >
                {taxRatesTableCard}
              </div>
            </div>
            <EntityNarrowSidePanelChrome
              panelOpen={showTaxRatesPromoNarrowPanel}
              onOpenPanel={() => setShowTaxRatesPromoNarrowPanel(true)}
              onClosePanel={() => setShowTaxRatesPromoNarrowPanel(false)}
              tabLabel={t('organisation.tax-rates-side-panel-tab')}
              panelWidthPx={TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH}
            >
              <TaxRatesPromotionBanner
                organisation={organisation}
                cardWidth={TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH}
              />
            </EntityNarrowSidePanelChrome>
          </div>
        )}
      </div>
    </div>
  );
};
